import { useEffect, useRef, useState } from "react";
import { BriaImageProps } from "../../../components/common/Galleries/BriaImage";
import { FIBO_DEFAULT_NUM_RESULTS, FIBO_DEFAULT_STEPS } from "../../../constants/FiboConstants";
import { POSTHOG_EVENTS } from "../../../constants/PostHogEvents";
import RouterConstants from "../../../constants/RouterConstants";
import { IMAGE_REFERENCE_CONFIGS } from "../../../constants/TextToImageConstants";
import { USER_SETTINGS } from "../../../constants/UserConstants";
import { usePostHog } from "../../../hooks/usePostHog";
import { useAppStore } from "../../../hooks/useStores";
import { ImageEditingViewMode, PlaygroundImage } from "../../../models/image-to-image";
import { SyntheticEventProps, TextToImageConfigType } from "../../../models/text-to-image";
import { createFileList, getFiboLabVariant, isFibo2App, urlToFile } from "../../../utils";
import { PlaygroundVideoProps } from "../../Playground/VideoGeneration/Video/PlaygroundVideo";

export const useFiboController = () => {
	const { playgroundStore, textToImageStore, fiboStore, authStore } = useAppStore();
	const { trackEvent } = usePostHog();
	const labVariant = getFiboLabVariant();

	const activeTab = fiboStore.activeTab;
	const setActiveTab = fiboStore.setActiveTab;
	// The refine box's draft survives hopping to generate and back, but it belongs to
	// the result it was typed for. Restoring it for any other result silently attaches
	// an old instruction to a new image, so the draft is keyed by result id.
	const savedRefinePromptRef = useRef<{ resultId: string | undefined; text: string }>({
		resultId: undefined,
		text: "",
	});
	const savedGenerateConfigRef = useRef<TextToImageConfigType | undefined>(undefined);
	const savedImageReferenceRef = useRef<PlaygroundImage | undefined>(undefined);
	const [showTryRefinePopup, setShowTryRefinePopup] = useState<boolean>(false);
	const lastAutoSwitchedResultRef = useRef<string | null>(null);

	const isSuccessfulImage = (img: PlaygroundImage & BriaImageProps) => {
		const hasUrl = typeof img.url === "string" && img.url.trim() !== "";
		const hasError = Boolean(img.error) || Boolean((img as any).errorType);
		return hasUrl && !hasError;
	};

	// Find the first result with at least one successful image
	const completedResultWithSuccess = (playgroundStore.playgroundResults ?? []).find((res) => {
		const imgs = res?.images ?? [];
		if (imgs.length === 0) return false;

		return imgs.some((img) => isSuccessfulImage(img as PlaygroundImage & BriaImageProps));
	});

	const isRefineTabEnabled = Boolean(completedResultWithSuccess);

	const restoreGenerateConfig = () => {
		const savedConfig = savedGenerateConfigRef.current as TextToImageConfigType;
		const savedRef = savedImageReferenceRef.current;

		if (savedConfig) {
			textToImageStore.config = savedConfig;
			textToImageStore.imageReference = savedRef || { id: "", url: "", playground_result_id: "" };
		} else {
			textToImageStore.clearConfig();
			textToImageStore.handleConfigChange("seed", undefined);
		}
	};

	const snapshotGenerateConfig = () => {
		savedGenerateConfigRef.current = textToImageStore.config;
		savedImageReferenceRef.current = textToImageStore.imageReference;
	};

	const applyRefineStateFromSelection = () => {
		const group = fiboStore.getSelectedFiboGroup?.();
		const result = playgroundStore.selectedResult;

		textToImageStore.handleDeleteImageReference();

		const baseCfg = (result?.config || {}) as Partial<TextToImageConfigType>;

		const effectiveSeed = group?.seed ?? baseCfg.seed;
		const effectiveStructuredPrompt = group?.structured_prompt ?? baseCfg.structured_prompt;
		const effectiveDiff = group?.structured_prompt_diff ?? baseCfg.structured_prompt_diff;

		// Ensure original_structured_prompt is set if it doesn't exist (needed for modification tracking)
		// Set it to the current structured_prompt value before any updates
		if (group && effectiveStructuredPrompt && !group.original_structured_prompt) {
			fiboStore.updateFiboResultGroup(group.id, {
				original_structured_prompt: effectiveStructuredPrompt,
			});
		}

		const savedDraft = savedRefinePromptRef.current;
		const refineDraft = savedDraft.resultId && savedDraft.resultId === result?.id ? savedDraft.text : "";

		textToImageStore.config = {
			...baseCfg,
			prompt: refineDraft,
			seed: effectiveSeed,
			structured_prompt: effectiveStructuredPrompt,
			text_guidance_scale: baseCfg.text_guidance_scale,
		} as TextToImageConfigType;

		fiboStore.handleStructuredPromptChange(effectiveStructuredPrompt ?? "");
		fiboStore.handleStructuredPromptDiffChange(group?.is_refine ? effectiveDiff : undefined);

		if (effectiveSeed !== undefined) {
			textToImageStore.handleConfigChange("seed", effectiveSeed);
		}
	};

	const handleTabSwitch = (tab: "generate" | "refine") => {
		if (tab === activeTab) return;
		if (tab === "refine" && !isRefineTabEnabled) return;

		if (tab === "refine") {
			snapshotGenerateConfig();
			applyRefineStateFromSelection();
		} else {
			savedRefinePromptRef.current = {
				resultId: playgroundStore.selectedResult?.id,
				text: textToImageStore?.config?.prompt ?? "",
			};
			restoreGenerateConfig();
		}

		setActiveTab(tab);

		// Track tab switch
		if (labVariant) {
			trackEvent(POSTHOG_EVENTS.FIBO_TAB_SWITCHED, {
				lab_variant: labVariant,
				from_tab: activeTab,
				to_tab: tab,
			});
		}
	};

	const handleResultInView = (id: string) => {
		playgroundStore.selectedResult = playgroundStore.playgroundResults.find((r) => r.id === id) || null;
	};

	const handleJumpTo = (
		resultId: string,
		selectedImage: PlaygroundImage | PlaygroundVideoProps,
		jumpToSection: (sectionId: string) => void,
	) => {
		jumpToSection(resultId);

		playgroundStore.selectedResult = playgroundStore.playgroundResults.find((r) => r.id === resultId) || null;

		savedRefinePromptRef.current = { resultId: undefined, text: "" };
		textToImageStore.handleConfigChange("prompt", "");

		if (isRefineTabEnabled) {
			snapshotGenerateConfig();
			applyRefineStateFromSelection();
			setActiveTab("refine");
		}

		playgroundStore.handleSelectImage(selectedImage as PlaygroundImage);
		fiboStore.setLastGeneratedResultId(resultId);

		// Track experiment jump
		if (labVariant) {
			trackEvent(POSTHOG_EVENTS.FIBO_EXPERIMENT_JUMPED_TO, {
				lab_variant: labVariant,
				result_id: resultId,
			});
		}
	};

	const handleTryRefineClick = () => {
		handleTabSwitch("refine");
		handleTryRefinePopupClose();
		setShowTryRefinePopup(false);
	};

	const handleTryRefinePopupClose = () => {
		authStore.user?.updateSettings(USER_SETTINGS.hideTryRefinePopup, true);
		setShowTryRefinePopup(false);
	};

	const handleReferenceImageClick = async (imageUrl: string) => {
		try {
			// Track reference image click
			if (labVariant) {
				trackEvent(POSTHOG_EVENTS.FIBO_REFERENCE_IMAGE_CLICKED, {
					lab_variant: labVariant,
				});
			}

			// Switch to generate tab
			handleTabSwitch("generate");

			// Convert URL to File and upload
			// Extract filename with extension, or use default with .jpg
			let filename = imageUrl.split("/").pop()?.split("?")[0] || "reference_image.jpg";

			// Ensure filename has a proper extension
			if (!filename.match(/\.(png|jpg|jpeg|webp)$/i)) {
				filename = filename + ".jpg";
			}

			const file = await urlToFile(imageUrl, filename);

			if (file) {
				const extension = filename.split(".").pop()?.toLowerCase();
				const mimeType = extension === "png" ? "image/png" : extension === "webp" ? "image/webp" : "image/jpeg";

				const correctedFile = new File([file], filename, { type: mimeType });

				const syntheticEvent: SyntheticEventProps = {
					target: {
						files: createFileList([correctedFile]),
						value: "",
					},
				};
				await textToImageStore.handleUploadImage(syntheticEvent);
			}
		} catch (error) {
			console.error("Error processing reference image:", error);
		}
	};

	// Set image editing view mode
	useEffect(() => {
		playgroundStore.imageEditingViewMode = ImageEditingViewMode.SINGLE;
		playgroundStore.clearResults();
		textToImageStore.clearConfig();
		textToImageStore.handleConfigChange("num_results", isFibo2App() ? 1 : FIBO_DEFAULT_NUM_RESULTS);
		textToImageStore.handleConfigChange("steps_num", FIBO_DEFAULT_STEPS);
		const urlParams = new URLSearchParams(window.location.search);
		const modelParam = urlParams.get("model");
		const isFiboLiteRoute = window.location.pathname.endsWith(RouterConstants.LABS.FIBO_LITE.path);
		if (modelParam === "fibo_lite" || isFiboLiteRoute) {
			textToImageStore.config = {
				...textToImageStore.config,
				model: "fibo_lite",
				model_id: "fibo_lite",
				model_version: "lite",
				steps_num: IMAGE_REFERENCE_CONFIGS.steps.fibo_lite.default,
			};
		}
	}, []);

	// Auto-switch to refine tab when selected result gets its first successful image
	const selectedResultId = playgroundStore.selectedResult?.id;
	const selectedResultHasSuccess = playgroundStore.selectedResult?.images?.some((img) =>
		isSuccessfulImage(img as PlaygroundImage & BriaImageProps),
	);

	useEffect(() => {
		if (selectedResultId && selectedResultHasSuccess && lastAutoSwitchedResultRef.current !== selectedResultId) {
			lastAutoSwitchedResultRef.current = selectedResultId;
			handleTabSwitch("refine");
		}
	}, [selectedResultId, selectedResultHasSuccess]);

	// Manage playground results selection
	useEffect(() => {
		const results = playgroundStore.playgroundResults;
		if (results.length === 0) {
			if (playgroundStore.selectedResult) playgroundStore.selectedResult = null;
			return;
		}

		if (!playgroundStore.selectedResult) {
			const first = results[0];
			if (first?.id) playgroundStore.selectedResult = first;
			return;
		}

		const latest = results[results.length - 1];
		if (!latest?.id) return;

		const isNewResult = latest.id !== playgroundStore.selectedResult?.id;

		if (isNewResult) {
			playgroundStore.selectedResult = latest;
		}
	}, [playgroundStore.playgroundResults, playgroundStore]);

	useEffect(() => {
		const loadPopupSettings = async () => {
			if (authStore.user) {
				const hideTryRefinePopup = await authStore.user?.getFreshSettingsCopy(USER_SETTINGS.hideTryRefinePopup);
				if (hideTryRefinePopup) return;
				if (
					!hideTryRefinePopup &&
					playgroundStore.playgroundResults.length === 1 &&
					!textToImageStore.isGeneratingImages &&
					!fiboStore.isGeneratingStructuredPrompt &&
					isRefineTabEnabled
				) {
					setShowTryRefinePopup(true);
				}
			}
		};

		loadPopupSettings();
	}, [
		authStore.user,
		playgroundStore.playgroundResults,
		textToImageStore.isGeneratingImages,
		fiboStore.isGeneratingStructuredPrompt,
		isRefineTabEnabled,
	]);

	return {
		// State
		activeTab,
		isRefineTabEnabled,
		showTryRefinePopup,

		// Actions
		handleTabSwitch,
		handleResultInView,
		handleJumpTo,
		handleTryRefineClick,
		handleTryRefinePopupClose,
		handleReferenceImageClick,
	};
};
